import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';

const Coupons = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [searchKeyword, setSearchKeyword] = useState('');

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: searchKeyword
      };
      
      const response = await apiService.post('coupons', params);
      if (response && response.data) {
        setCoupons(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load coupons');
      console.error('Coupons fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, searchKeyword]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCoupons();
    }, searchKeyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchCoupons]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setCurrentPage(1);
  };

  const toggleCouponStatus = async (couponId) => {
    try {
      const response = await apiService.get(`couponStatus/${couponId}`);
      if (!response.success) {
        toast.success('Coupon status updated successfully');
        // Update local state instead of refetching
        setCoupons(prevCoupons => 
          prevCoupons.map(coupon => 
            coupon._id === couponId 
              ? { ...coupon, isActive: !coupon.isActive }
              : coupon
          )
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to update coupon status');
    }
  };

  const deleteCoupon = async (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        const response = await apiService.get(`deleteCoupon/${couponId}`);
        if (!response.success) {
          toast.success('Coupon deleted successfully');
          fetchCoupons();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error('Failed to delete coupon');
      }
    }
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 p-6 border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-bold text-gray-900">Coupons</h1>
              <button
                onClick={() => navigate('/coupon/create')}
                className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 transition-colors"
              >
                <i className="bi bi-plus-circle mr-2"></i>
                Add Coupon
              </button>
            </div>
            
            {/* Search */}
            <div className="relative w-64">
              <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search coupons..."
                value={searchKeyword}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applicable For</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid From</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
                      </div>
                    </td>
                  </tr>
                ) : coupons.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-4 text-center text-gray-500">
                      No coupons found
                    </td>
                  </tr>
                ) : (
                  coupons.map((coupon, index) => (
                    <tr key={coupon._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {((currentPage - 1) * recordsPerPage) + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-12 h-12">
                          {coupon.image ? (
                            <img 
                              src={`${process.env.REACT_APP_MEDIA_URL}${coupon.image}`} 
                              alt="Coupon" 
                              className="w-full h-full object-cover rounded"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                              <i className="bi bi-image text-gray-400"></i>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold bg-[#009ef7]/10 text-[#009ef7] rounded">
                          {coupon.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {coupon.bonusType === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {coupon.bonusType === 'percentage' ? `${coupon.bonusValue}%` : `₹${coupon.bonusValue}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {coupon.applicableFor === 'all' && (
                          <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded">All Users</span>
                        )}
                        {coupon.applicableFor === 'first' && (
                          <span className="px-2 py-1 text-xs font-semibold bg-[#009ef7]/10 text-[#009ef7] rounded">First Order</span>
                        )}
                        {coupon.applicableFor === 'signup' && (
                          <span className="px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded">New Signup</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(coupon.validFrom).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(coupon.validUntil).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={coupon.isActive}
                            onChange={() => toggleCouponStatus(coupon._id)}
                            className="sr-only"
                          />
                          <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            coupon.isActive ? 'bg-[#009ef7]' : 'bg-gray-200'
                          }`}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              coupon.isActive ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </div>
                        </label>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate(`/coupon/edit/${coupon._id}`)}
                            className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteCoupon(coupon._id)}
                            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Coupons;